import React, { useMemo } from 'react';

const _API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const cmsUrl = (u) => {
  if (!u) return '';
  if (/^https?:\/\//.test(u)) return u;
  if (u.startsWith('/api/')) return `${_API_BASE}${u}`;
  return u;
};

/**
 * Continuous film-strip carousel. Two copies of the slide list scroll
 * side-by-side using a single CSS keyframe animation, giving the illusion
 * of an infinite loop with no JS timers. Pause-on-hover is opt-in via the
 * `pauseOnHover` prop (set from CMS `cms.hero_slides.pause_on_hover`).
 *
 * Renders nothing when there are zero slides — the CMS toggle gates the
 * whole section in Landing.js so we don't need to render an empty band.
 */
export const HeroFilmStripSlides = ({ config }) => {
  const slides = useMemo(() => {
    const raw = (config?.slides || []).slice();
    raw.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    return raw.filter((s) => s.image_url);
  }, [config]);

  if (!config || !slides.length) return null;

  const speedMs = Number(config.scroll_speed_ms) || 40000;
  // iter-105: support a `transparent` background type so the strip can float
  // directly over whatever is behind the section.
  const bgType = ['solid', 'gradient', 'transparent'].includes(config.background_type)
    ? config.background_type : 'solid';
  const bg =
    bgType === 'gradient'
      ? `linear-gradient(135deg, ${config.background_gradient_start || '#FFDEE9'} 0%, ${config.background_gradient_end || '#B5FFFC'} 100%)`
      : bgType === 'transparent'
      ? 'transparent'
      : (config.background_color || '#FFFDF8');

  // Duplicate the slide array so the keyframe can scroll one full copy
  // off-screen before resetting — eliminating the visible "jump" you'd get
  // with a single-copy carousel.
  const looped = [...slides, ...slides];

  return (
    <section
      className="relative py-10 sm:py-12 overflow-hidden"
      style={{ background: bg }}
      data-testid="hero-film-strip-section"
    >
      {(config.section_title || config.section_subtitle) && (
        <div className="container mx-auto px-4 mb-6 text-center">
          {config.section_title && (
            <h2
              className="text-2xl sm:text-3xl font-semibold text-[#1E1E24]"
              style={{ fontFamily: 'Fredoka, sans-serif' }}
              data-testid="hero-film-strip-title"
            >
              {config.section_title}
            </h2>
          )}
          {config.section_subtitle && (
            <p className="text-sm text-[#6B6B7A] mt-1" data-testid="hero-film-strip-subtitle">
              {config.section_subtitle}
            </p>
          )}
        </div>
      )}

      <div
        className={`flex gap-4 sm:gap-6 hero-filmstrip-track ${config.pause_on_hover ? 'hero-filmstrip-hoverable' : ''}`}
        style={{ animationDuration: `${speedMs}ms` }}
        data-testid="hero-film-strip-track"
      >
        {looped.map((slide, idx) => (
          <figure
            key={`${slide.id || idx}-${idx}`}
            className="hero-filmstrip-card flex-shrink-0 w-[260px] sm:w-[320px] rounded-2xl overflow-hidden shadow-lg bg-white border border-white"
            data-testid={idx < slides.length ? `hero-film-strip-slide-${slide.id || idx}` : undefined}
          >
            <div className="w-full aspect-[4/3] bg-[#F3F0EA]">
              <img
                src={cmsUrl(slide.image_url)}
                alt={slide.caption || `Slide ${idx + 1}`}
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
            {slide.caption && (
              <figcaption
                className="px-4 py-3 text-sm text-[#1E1E24]"
                style={{ fontFamily: 'Fredoka, sans-serif' }}
              >
                {slide.caption}
              </figcaption>
            )}
          </figure>
        ))}
      </div>
    </section>
  );
};

export default HeroFilmStripSlides;
